/**
 * 15 #9 — "From email": paste the consultant's reply email and the AI assistant reads the code (A / B / C / D), the
 * reply date and the comments for the submissions of this form. It only FILLS the form — nothing is saved until
 * the person checks it and presses Save reply. AI reading a consultant's email is an AI extra (`ai_extras`): OFF until
 * an admin switches it on in Admin › Integrations (owner decision), even where the AI page (`ai`) works. While it is
 * off (or not set up) the box says so and the comments can be pasted by hand. (Reading a shared mailbox automatically
 * is the reply-inbox job, which needs Incoming email as well.)
 */
import { useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NotConfigured } from '@/components/ui/not-configured';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { getErrorMessage } from '@/lib/errors';
import { AI_EXTRAS, FEATURE_STATUS, aiExtrasHelp, isFeatureNotEnabledError } from '@/lib/integrations';
import { sdRequest } from './api';

export function PasteEmail({ rows, onParsed, onClose }) {
  const ai = useFeature(AI_EXTRAS);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const read = async () => {
    if (text.trim().length < 10) { setError('Paste the whole email first'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await sdRequest('post', '/parse-reply-email', {
        text: text.trim(),
        items: rows.map((row) => ({ id: row.drawing.id, index: row.index, submission_id: row.submission.id || null })),
      });
      onParsed(res.data);
    } catch (err) {
      if (isFeatureNotEnabledError(err)) ai.refresh?.();
      setError(getErrorMessage(err, 'The AI could not read this email'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-[color:var(--brand-soft-border)] bg-[color:var(--navy-50)] p-2.5" data-testid="paste-email">
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-brand-ink" aria-hidden="true" />
        <p className="flex-1 text-[12px] font-bold text-ink">Read the reply from the email</p>
        <button type="button" onClick={onClose} className="grid h-6 w-6 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink" aria-label="Close" data-testid="paste-email-close">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      {ai.loading && !ai.enabled ? <p className="text-[12px] text-ink-3">Checking the AI assistant…</p> : null}
      {!ai.loading && !ai.enabled ? (
        <NotConfigured
          feature={AI_EXTRAS}
          layout="inline"
          title={ai.status === FEATURE_STATUS.OFF ? 'Reading emails with AI is switched off' : 'Reading emails needs the AI assistant'}
          text={aiExtrasHelp(ai, 'Copy the consultant’s comments into the box below and pick the code yourself.')}
          testId="paste-email-ai-off"
        />
      ) : null}
      {ai.enabled ? (
        <>
          <label className="block">
            <span className="sr-only">The consultant’s reply email</span>
            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Paste the consultant’s reply email (forwarded text is fine)…"
              maxLength={20000}
              className="h-24 w-full resize-y rounded-control border border-line bg-surface px-2.5 py-2 text-[12.5px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
              data-testid="paste-email-text"
            />
          </label>
          {error ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-ink-3">Nothing is saved until you press Save reply.</p>
            <Button type="button" size="xs" onClick={read} disabled={busy || !text.trim()} data-testid="paste-email-read">
              {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Sparkles aria-hidden="true" />}
              {busy ? 'Reading…' : 'Read with AI'}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
